/* digi-cookie-banner.js — DIGI module: cookie consent.
   On Shopify the choice is written through the Customer Privacy API, so declining really does stop
   Shopify's analytics and marketing pixels. Off Shopify the same file still works: it falls back to
   localStorage and fires a `digi:consent` event other scripts can listen for. */
(() => {
  const cfgEl = document.getElementById('digi-cookie-config');
  if (!cfgEl || document.querySelector('.digi-cc')) return;
  const cfg = JSON.parse(cfgEl.textContent);
  const dec = (v) => { const t = document.createElement('textarea'); t.innerHTML = v; return t.value; };
  const S = Object.fromEntries(Object.entries(cfg.strings).map(([k, v]) => [k, dec(v)]));
  const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const KEY = 'digi-consent';
  const CATS = ['analytics', 'marketing', 'preferences'];

  const read = () => { try { return JSON.parse(localStorage.getItem(KEY) || 'null'); } catch (e) { return null; } };
  const write = (v) => { try { localStorage.setItem(KEY, JSON.stringify(v)); } catch (e) {} };

  /* ---- Shopify Customer Privacy ---- */
  const shopifyPrivacy = () => new Promise((resolve) => {
    if (!window.Shopify || typeof window.Shopify.loadFeatures !== 'function') return resolve(null);
    if (window.Shopify.customerPrivacy) return resolve(window.Shopify.customerPrivacy);
    window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }],
      (err) => resolve(err ? null : window.Shopify.customerPrivacy || null));
  });

  const apply = async (consent) => {
    write({ v: cfg.version, at: new Date().toISOString(), consent });
    const api = await shopifyPrivacy();
    if (api && typeof api.setTrackingConsent === 'function') {
      api.setTrackingConsent({
        analytics: !!consent.analytics,
        marketing: !!consent.marketing,
        preferences: !!consent.preferences,
        sale_of_data: !!consent.marketing,
      }, () => {});
    }
    document.dispatchEvent(new CustomEvent('digi:consent', { detail: consent }));
  };

  /* ---- markup ---- */
  const group = (key, on, locked) => `
    <label class="digi-cc__group">
      <input type="checkbox" data-digi-cc-cat="${key}" ${on ? 'checked' : ''} ${locked ? 'disabled' : ''}>
      <span><span class="digi-cc__group-name">${esc(S[key])}</span>
      <span class="digi-cc__group-note">${esc(S[key + '_note'])}</span></span>
    </label>`;

  const el = document.createElement('section');
  el.className = 'digi-cc' + (cfg.position === 'corner' ? ' digi-cc--corner' : '');
  el.setAttribute('role', 'region');
  el.setAttribute('aria-label', S.title);
  el.setAttribute('tabindex', '-1');
  const policy = cfg.policy ? ` <a href="${esc(cfg.policy)}">${esc(S.policy_link)}</a>` : '';
  el.innerHTML = `
    <div class="digi-cc__inner">
      <div class="digi-cc__copy">
        <p class="digi-cc__title">${esc(S.title)}</p>
        <p class="digi-cc__text">${esc(S.text)}${policy}</p>
      </div>
      <div class="digi-cc__actions">
        ${cfg.granular ? `<button type="button" class="digi-cc__btn digi-cc__btn--link" data-digi-cc-prefs aria-expanded="false">${esc(S.prefs)}</button>` : ''}
        <button type="button" class="digi-cc__btn" data-digi-cc-reject>${esc(S.reject)}</button>
        <button type="button" class="digi-cc__btn digi-cc__btn--primary" data-digi-cc-accept>${esc(S.accept)}</button>
      </div>
      ${cfg.granular ? `<div class="digi-cc__prefs" hidden data-digi-cc-panel>
        ${group('necessary', true, true)}
        ${CATS.map((c) => group(c, false, false)).join('')}
        <div class="digi-cc__actions" style="margin-block-start:1.2rem">
          <button type="button" class="digi-cc__btn digi-cc__btn--primary" data-digi-cc-save>${esc(S.save)}</button>
        </div>
      </div>` : ''}
    </div>`;

  /* `moveFocus` only when the visitor asked for the panel. Taking focus on page load is its own
     accessibility fault: the keyboard journey should start at the top of the page, and anything that
     checks `document.activeElement !== document.body` (the scent quiz does) is thrown off by it. */
  const show = (openPrefs, moveFocus) => {
    if (!el.isConnected) document.body.appendChild(el);
    el.setAttribute('data-open', '');
    if (openPrefs) togglePrefs(true);
    if (moveFocus) el.focus({ preventScroll: true });
  };
  const hide = () => { el.removeAttribute('data-open'); };
  const togglePrefs = (open) => {
    const panel = el.querySelector('[data-digi-cc-panel]');
    const btn = el.querySelector('[data-digi-cc-prefs]');
    if (!panel || !btn) return;
    panel.hidden = !open;
    btn.setAttribute('aria-expanded', String(open));
  };

  el.addEventListener('click', async (e) => {
    if (e.target.closest('[data-digi-cc-accept]')) {
      await apply({ analytics: true, marketing: true, preferences: true }); return hide();
    }
    if (e.target.closest('[data-digi-cc-reject]')) {
      await apply({ analytics: false, marketing: false, preferences: false }); return hide();
    }
    if (e.target.closest('[data-digi-cc-prefs]')) {
      return togglePrefs(el.querySelector('[data-digi-cc-panel]').hidden);
    }
    if (e.target.closest('[data-digi-cc-save]')) {
      const consent = {};
      CATS.forEach((c) => { consent[c] = !!el.querySelector(`[data-digi-cc-cat="${c}"]`)?.checked; });
      await apply(consent); return hide();
    }
  });
  el.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && !el.querySelector('[data-digi-cc-panel]')?.hidden) togglePrefs(false);
  });

  /* anything with data-digi-cookies-open reopens it — the "withdraw consent" link */
  document.addEventListener('click', (e) => {
    const t = e.target.closest('[data-digi-cookies-open]');
    if (!t) return;
    e.preventDefault();
    const stored = read();
    if (stored) CATS.forEach((c) => {
      const box = el.querySelector(`[data-digi-cc-cat="${c}"]`);
      if (box) box.checked = !!stored.consent?.[c];
    });
    show(true, true);
  });

  const start = () => {
    const stored = read();
    if (stored && String(stored.v) === String(cfg.version)) {
      apply(stored.consent);          /* re-assert on every page load, the API is per-session */
      return;
    }
    show(false, false);
  };
  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', start) : start();
})();
